/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  AlertTriangle,
  Info,
  CheckCircle2,
  MessageSquareWarning,
} from "lucide-react";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { cn } from "@/lib/utils";

interface NotificationItem {
  id: string;
  kind: "warn" | "info" | "action";
  title: string;
  body: string;
  href: string;
  count?: number;
}

interface NotificationsResponse {
  items: NotificationItem[];
  count: number;
  actionCount: number;
}

const kindStyles: Record<
  NotificationItem["kind"],
  { bg: string; iconBg: string; iconColor: string; icon: React.ReactNode }
> = {
  warn: {
    bg: "bg-amber-50 border-amber-100",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  action: {
    bg: "bg-red-50 border-red-100",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    icon: <MessageSquareWarning className="h-4 w-4" />,
  },
  info: {
    bg: "bg-blue-50 border-blue-100",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    icon: <Info className="h-4 w-4" />,
  },
};

/**
 * Floating notification bell — top-right of the Layout. Polls the
 * /admin/dashboard/notifications feed every 60s and surfaces a red
 * counter badge when there's actionable work. Clicking opens a
 * dropdown panel with each item linking to the relevant admin page.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery<NotificationsResponse>({
    queryKey: ["admin", "notifications"],
    queryFn: () => apiClient.get(API_ENDPOINTS.DASHBOARD.NOTIFICATIONS),
    // Shape-coerce so a temporary server hiccup never crashes the bell.
    select: (r: any) => ({
      items: Array.isArray(r?.items) ? r.items : [],
      count: typeof r?.count === "number" ? r.count : 0,
      actionCount:
        typeof r?.actionCount === "number" ? r.actionCount : 0,
    }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const items = data?.items ?? [];
  const badgeCount = data?.actionCount ?? 0;

  return (
    <div
      ref={rootRef}
      className="fixed bottom-6 right-6 z-40"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all flex items-center justify-center",
          open && "ring-2 ring-blue-400 border-blue-400",
        )}
        title="Notifications"
      >
        <Bell className="h-4 w-4 text-gray-600" />
        {badgeCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white">
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 bottom-12 w-[340px] max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-xl border border-gray-200 py-2">
          <div className="px-4 py-2 flex items-center justify-between border-b border-gray-100">
            <p className="text-sm font-bold text-gray-900">Notifications</p>
            <span className="text-[11px] text-gray-400 font-medium">
              {items.length} item{items.length === 1 ? "" : "s"}
            </span>
          </div>

          {items.length === 0 ? (
            <div className="py-10 flex flex-col items-center gap-2 text-gray-400">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              <p className="text-sm font-semibold text-gray-700">
                You&apos;re all caught up
              </p>
              <p className="text-xs text-gray-400">
                Nothing needs attention right now.
              </p>
            </div>
          ) : (
            <div className="py-1">
              {items.map((item) => {
                const s = kindStyles[item.kind];
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "block mx-2 my-1 rounded-lg border px-3 py-2.5 hover:shadow-sm transition-shadow",
                      s.bg,
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        className={cn(
                          "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
                          s.iconBg,
                          s.iconColor,
                        )}
                      >
                        {s.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {item.title}
                          </p>
                          {item.count !== undefined && item.count > 0 && (
                            <span className="text-[10px] font-extrabold bg-gray-900 text-white px-1.5 py-0.5 rounded-full">
                              {item.count}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
